import { matchPath } from "react-router";

import { routerLocationChange } from "Reducers/app/routerLocationChange";
import { startAppListening } from "Store/listenerMiddleware";

import { loadTaskActivities } from "./thunks/loadTaskActivities.thunk";

import type { AppStartListening } from "Store/listenerMiddleware";

/**
 * Task detail Activity section — the tab AND its bookmarkable log deep-link
 * (`.../activity/log/:activityId`). The trailing `/*` splat covers both: the Run
 * modal deep-links a fresh run straight into its log, never touching the bare
 * tab, so matching the tab alone left the list unloaded and the live websocket
 * merge with no bucket to write into — the first run stayed stuck and absent
 * until reload (CON-1436).
 *
 * The data router only matches down to `:organizationId/*`; project/environment/
 * task are rendered via descendant `<Routes>`, so `routerLocationChange.params`
 * never carries them. We re-derive them from the full `location.pathname` with
 * the same matcher the router uses (cf. `git.listeners`).
 */
const TASK_ACTIVITY_ROUTE_PATTERN =
  "/:organizationId/:projectId/:environmentId/tasks/:taskName/activity/*";

/**
 * Loads the environment's task activities when the user opens a task's Activity
 * tab, so the view fills without the component dispatching on mount (no
 * `useEffect` data fetching — see the frontend Redux guidelines). Lives with the
 * `taskActivities` slice it populates; the route is only the trigger. Scoped to
 * the `/activity` leaf so the Overview/YAML tabs don't fetch activities they
 * never show.
 */
export const registerTaskActivitiesListeners = (
  startListening: AppStartListening
) => {
  startListening({
    actionCreator: routerLocationChange,
    effect: (action, { dispatch, getState }) => {
      const match = matchPath(
        TASK_ACTIVITY_ROUTE_PATTERN,
        action.payload.location.pathname
      );

      if (!match) return;

      const { projectId, environmentId } = match.params;
      if (!projectId || !environmentId) return;

      // Load once per environment. The project websocket keeps a loaded bucket
      // live (see `taskActivities.slice`), so re-fetching on every navigation
      // within the section — including the log's line-selection hash changes,
      // each of which mints a new location — would only churn the network and
      // flash `loading`. Skip when a bucket already exists; a failed or pending
      // initial load leaves none, so it still retries.
      const alreadyLoaded = Boolean(
        getState().taskActivities?.data?.[projectId]?.[environmentId]
      );
      if (alreadyLoaded) return;

      dispatch(loadTaskActivities({ projectId, environmentId }));
    }
  });
};

// Register against the production middleware singleton.
registerTaskActivitiesListeners(startAppListening);
